import { useSelector } from 'react-redux';

import { updateLoginPreferences } from 'api/preferences';
import { useAction } from 'hooks/useAction';
import { usePushSiteError } from 'hooks/usePushSiteError';
import { updateLoginPreferences as prefsAction } from 'containers/App/actions';
import {
  CALENDAR_VIEW_OPTIONS,
} from 'utils/constants';

/* eslint-disable max-len */
/**
 * @typedef {Object} CalendarPrefs
 * @property {string} firstDayOfWeek - The day that appears in the leftmost column of the calendar.
 * @property {boolean} showChecklist - Whether to show checklist tasks events.
 * @property {boolean} showEvents - Whether to show project events.
 * @property {boolean} showPayments - Whether to show payment reminder events.
 * @property {boolean} showBookings - Whether to show booking events.
 * @property {boolean} showProjects - Whether to show booked project events.
 * @property {Array} weddingExclusions - A list of project ids that should not display in the calendar.
 * @property {Object} externalCalendarsDisplay - An object of keys of external calendars with a boolean value for display.
 * @property {Object} microfilters - An object of microfilters applied to the calendar. See utils/constants for object schema.
 */

/**
 * @typedef {Object} PreferencesHook
 * @property {Object} preferences - The current login preferences.
 * @property {Array} leads - Preferences specific to leads index.
 * @property {Object} notes - Preferences specific to project notes index.
 * @property {Array} savedColors - Preferences specific to colors saved in the color picker.
 * @property {Object} weddings - Preferences specific to project index.
 * @property {Array} weddingsDownload - Preferences specific to projects download report.
 * @property {CalendarPrefs} calendar - Preferences specific to calendar.
 * @property {Function} updatePrefByKey - Function that updates a single preference by key.
 * @property {Function} toggleLeadsPrefs - Function that toggles leads index preferences.
 * @property {Function} updateNotesPrefs - Function that updates the notes preferences.
 * @property {Function} updateWeddingPrefs - Function that updates the projects index preferences.
 */
/* eslint-enable max-len */

/**
 * A hook that returns login preferences and functions to update the preferences.
 * @returns {PreferencesHook}
 */
export const usePrefs = () => {
  const preferences = useSelector((state) => state.global.userInfo.preferences);
  const {
    leads,
    notes,
    savedColors,
    weddings,
    weddingsDownload,
  } = preferences || {};
  // Calendar preferences have historically used calendar.${prefName} naming, so we'll stick with that
  // unless we decide to rework how we name and store preferences.
  const calendar = {
    ...CALENDAR_VIEW_OPTIONS,
    ...Object.keys(preferences).reduce((acc, key) => {
      if (key.includes('calendar')) {
        acc[key] = preferences[key];
      }
      return acc;
    }, {}),
  };

  const {
    'calendar.firstDayOfWeek': firstDayOfWeek,
    'calendar.showChecklist': showChecklist,
    'calendar.showEvents': showEvents,
    'calendar.showPayments': showPayments,
    'calendar.showBookings': showBookings,
    'calendar.showProjects': showProjects,
    'calendar.weddingExclusions': weddingExclusions = [],
    'calendar.externalCalendarsDisplay': externalCalendarsDisplay = {},
    'calendar.microfilters': microfilters = {},
  } = calendar || {};

  const updatePrefsState = useAction(prefsAction);
  const pushSiteError = usePushSiteError();

  // Updates the redux store immediately, but rolls back if the request fails.
  const updatePrefs = async (newPrefs) => {
    const oldPrefs = { ...preferences };
    updatePrefsState({ ...oldPrefs, ...newPrefs });
    const response = await updateLoginPreferences(newPrefs);

    if (!response.ok) {
      updatePrefsState(oldPrefs);
      pushSiteError("Your preferences couldn't be updated. Please try again in a moment.");
    }
  };

  /**
   * Updates a preference by key. Many preferences are just a flat key with a value. This
   * function will only update one preference at a time to prevent race conditions that could
   * overwrite other simultaneous calls.
   * @param {*} key - The configurable preference.
   * @param {*} value - The value of the preference.
   * @returns {null}
   */
  const updatePrefByKey = (key, value) => {
    updatePrefs({ [key]: value });
  };

  /**
   * Toggles leads preferences in the leads index.
   * Leads preferences are a list of id and names with a boolean to denote if the
   * column in the lead index is displayed. The integrity of list should be maintained
   * with only the checked value being able to be changed.
   * @param {string} id - The name of leads index column to toggle
   * @param {boolean} [checked] - The optional value column's checked status to override toggle.
   * @returns {null}
   */
  const toggleLeadsPrefs = (id, checked) => {
    // #TODO: There are custom rules that certain lead prefs being toggled will
    // toggle others as a set. Those need to be implemented if the leads settings
    // are refactored using this hook.
    const newLeads = leads;
    if (checked) {
      newLeads[id] = checked;
    } else {
      newLeads[id] = !newLeads[id];
    }

    updatePrefs({ leads: newLeads });
  };

  /**
   * Updates notes preferences in the notes index.
   * @param {'filter'|'view'} key - The configurable preferences.
   * @param {string} value - The value for the preference by key.
   * @returns {null}
   */
  const updateNotesPrefs = (key, value) => {
    const newNotes = notes;
    newNotes[key] = value;

    updatePrefs({ notes: newNotes });
  };

  /**
   * Updates various preferences on the projects index. Each key can have different
   * types of values, so the function itself can handle validation. This should be wrapped
   * in a try/catch because it will throw an error when validation fails.
   * @param {*} key - The configurable preference.
   * @param {*} value - The value of the preference.
   */
  const updateWeddingsPrefs = (key, value) => {
    const newWeddings = weddings;
    switch (key) {
      case 'filter': // This preference is deprecated.
        // Falls through.
      case 'sort':
        // Falls through.
      case 'sortArchive':
        // Falls through.
      case 'view':
        if (typeof value !== 'string') throw new Error('value must be a string value');
        newWeddings[key] = value;
        break;
      case 'leadId': // This preference is deprecated.
        if (typeof value !== 'number') throw new Error('leadId must be a number value');
        newWeddings[key] = value;
        break;
      case 'location':
        // Falls through.
      case 'projectLead':
        // Falls through.
      case 'service':
        if (typeof value !== 'boolean') throw new Error('value m ust be a boolean type.');
        newWeddings[key] = value;
        break;
      default:
        // No action.
        break;
    }

    updatePrefs({ weddings: newWeddings });
  };

  /**
   * Updates saved colors
   * This has it's own unique action that works pretty well as it stands so I won't bother
   * reimplementing it here for now. See src/containers/App/actions.js to see the action.
   * @returns {null}
   */
  // const updateSavedColors = () => {};

  /**
   * Updates project download preferences.
   * Project downloads have been removed for an unknown reason so these preferences aren't in
   * use at the moment. These will work similarly to leads preferences, where the items in the
   * array are only toggled with the checked property. When we bring back project downloads,
   * this can be implemented in a similar way.
   */
  // const updateWeddingsDownloadPrefs = () => {};

  /**
   * Updates a single calendar preference. Calendar preferences have historically used a
   * calendar prefix separated by a dot e.g. calendar.firstDayOfWeek, so this function
   * will append the prefix to keep it consistent with what has been done in the past.
   * @param {*} key - The configurable preference.
   * @param {*} value - The value of the preference.
   */
  const updateCalendarPrefs = (key, value) => {
    updatePrefByKey(`calendar.${key}`, value);
  };

  return {
    preferences,
    leads,
    notes,
    savedColors,
    weddings,
    weddingsDownload,
    calendar: {
      firstDayOfWeek,
      showChecklist,
      showEvents,
      showPayments,
      showBookings,
      showProjects,
      weddingExclusions,
      externalCalendarsDisplay,
      microfilters,
    },
    updatePrefByKey,
    toggleLeadsPrefs,
    updateNotesPrefs,
    updateWeddingsPrefs,
    updateCalendarPrefs,
  };
};
